import isEmpty from "lodash/isEmpty";
import { getCustomValueOptionId } from "PFCore/helpers/options/custom_values_options";
import {
  AutocompleteCustomValuesResponse,
  fetchAutocompleteCustomValues
} from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { fetchLocationsOptions } from "PFCore/services/locations";
import { CustomType, Id, PureCustomValue, PureCustomValueData } from "PFTypes";

type CreateCustomValueOptionsFetcherParams = {
  customType: CustomType | null;
  values: PureCustomValueData[];
  perPage: number;
  useGlobalValues?: boolean;
  connectedValuesIds: Id[];
  requiresSearch?: boolean;
};

const formatOptions = (response: PureCustomValue[] | AutocompleteCustomValuesResponse) => {
  const formattedEntries = response.map((customValue) => ({
    ...customValue,
    id: getCustomValueOptionId(customValue),
    value: String(customValue.text || customValue.value),
    original: customValue
  }));
  return {
    entries: formattedEntries,
    meta: { total: formattedEntries.length }
  };
};

export const createCustomValueOptionsFetcher =
  ({
    customType,
    values,
    perPage,
    useGlobalValues,
    connectedValuesIds,
    requiresSearch
  }: CreateCustomValueOptionsFetcherParams) =>
  async ({ searchTerm, ids }: { searchTerm: string; ids?: string[] }) => {
    const term = searchTerm;
    if (ids?.length) {
      return await Promise.resolve(formatOptions(values as PureCustomValue[]));
    }
    if (requiresSearch && !term && isEmpty(connectedValuesIds)) {
      return { entries: [], meta: { total: 0 } };
    }
    const type = customType!.name;

    if (customType?.value_type === "location") {
      const result =
        term.length < 3
          ? ([] as PureCustomValue[])
          : (await fetchLocationsOptions({ term, customTypeId: customType.id })).entries;
      return formatOptions(result);
    }

    const result = await fetchAutocompleteCustomValues({
      term,
      type,
      limit: perPage,
      ...(useGlobalValues ? { global: true } : {}),
      ...(!isEmpty(connectedValuesIds) ? { connectedOptions: true, customValueIds: connectedValuesIds } : {})
    });
    return formatOptions(result);
  };
